import React, { useEffect, useState } from "react";
import { Form, Button, Card, Col, Row, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSavePartner } from "../../api/mutations/useSavePartner";
import { useQueryClient } from "@tanstack/react-query";
import { Partner } from "../../types/Types";

interface PartnerFormData {
  id?: number;
  name: string;
  description: string;
  logo: FileList;
  backgroundColour: string;
  fontColour: string;
  websiteUrl: string;
}

interface ModalProps {
  show: boolean;
  onHide: () => void;
  defaultData?: Partner | null;
}

const CreatePartnerModal: React.FC<ModalProps> = ({
  onHide,
  show,
  defaultData,
}) => {
  const queryClient = useQueryClient();
  const { mutate: createPartner, isPending } = useSavePartner({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      reset();
      setLogoPreview(null);
      onHide();
    },
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<PartnerFormData>({
    defaultValues: {
      name: defaultData?.name || "",
      description: defaultData?.description || "",
      websiteUrl: defaultData?.websiteUrl || "",
      backgroundColour: defaultData?.backgroundColour || "#ffffff",
      fontColour: defaultData?.fontColour || "#000000",
    },
  });

  useEffect(() => {
    if (defaultData) {
      reset({
        name: defaultData.name || "",
        description: defaultData.description || "",
        websiteUrl: defaultData.websiteUrl || "",
        backgroundColour: defaultData.backgroundColour || "#ffffff",
        fontColour: defaultData.fontColour || "#000000",
      });

      if (defaultData.Logo) {
        setLogoPreview(defaultData.Logo); // backend logo URL
      }
      setLogoError(null);
    } else {
      reset({
        name: "",
        description: "",
        websiteUrl: "",
        backgroundColour: "#ffffff",
        fontColour: "#000000",
      });
      setLogoPreview(null);
      setLogoError(null);
    }
  }, [defaultData, reset]);

  const watchName = watch("name");
  const watchDescription = watch("description");
  const watchBg = watch("backgroundColour");
  const watchFont = watch("fontColour");

  const LOGO_ACCEPT =
    ".png,.jpg,.jpeg,.heif,.heic,image/png,image/jpeg,image/heif,image/heic";
  const LOGO_VALID_TYPES = [
    "image/png",
    "image/jpeg",
    "image/heif",
    "image/heic",
  ];
  const LOGO_MAX_SIZE = 5 * 1024 * 1024;

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setLogoError(null);

    if (!file) {
      setLogoPreview(defaultData?.Logo || null);
      setValue("logo", e.target.files as FileList, { shouldDirty: true });
      return;
    }

    if (!LOGO_VALID_TYPES.includes(file.type)) {
      setLogoError(
        "Unsupported File Format. Please upload an image (PNG, JPG, HEIF, or HEIC)."
      );
      e.target.value = "";
      setLogoPreview(defaultData?.Logo || null);
      return;
    }

    if (file.size > LOGO_MAX_SIZE) {
      setLogoError("File size must be less than 5MB");
      e.target.value = "";
      setLogoPreview(defaultData?.Logo || null);
      return;
    }

    setValue("logo", e.target.files as FileList, { shouldDirty: true });
    setLogoPreview(URL.createObjectURL(file));
  };

  const onSubmit = (data: PartnerFormData) => {
    if (logoError) {
      return;
    }
    if (!isDirty) {
      onHide();
      return;
    }

    const formData = new FormData();
    if (defaultData?.id) {
      formData.append("id", String(defaultData.id));
    }
    // Append text fields
    Object.entries(data).forEach(([key, value]) => {
      if (key === "logo") return; // skip file here
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    // Append logo if selected
    if (data.logo && data.logo.length > 0) {
      formData.append("logo", data.logo[0]);
    }

    formData.append("isEnabled", "1");

    // Save merged data locally (without file)
    createPartner(formData);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">Create Partner</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          {/* --- FORM --- */}
          <Col md={7}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Business Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter business name"
                  {...register("name", { required: true })}
                  className="shadow-sm"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter description"
                  {...register("description", { required: true })}
                  className="shadow-sm"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Partner URL</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://partner-website.com"
                  {...register("websiteUrl", { required: true })}
                  className="shadow-sm"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Logo</Form.Label>
                <Form.Control
                  type="file"
                  accept={LOGO_ACCEPT}
                  onChange={handleLogoChange}
                  className="shadow-sm"
                  isInvalid={!!logoError}
                />
                {logoError && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {logoError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Background Color
                    </Form.Label>
                    <Form.Control
                      type="color"
                      title="Pick background color"
                      {...register("backgroundColour")}
                      className="form-control-color"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Font Color</Form.Label>
                    <Form.Control
                      type="color"
                      title="Pick font color"
                      {...register("fontColour")}
                      className="form-control-color"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                type="submit"
                variant="primary"
                className="mt-2 px-4 shadow-sm rounded-pill"
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Partner"
                )}
              </Button>
            </Form>
          </Col>

          {/* --- REALTIME PREVIEW --- */}
          <Col
            md={5}
            className="mt-5 d-flex justify-content-center align-items-start"
          >
            <Card
              className="partner-preview-card text-center shadow-sm border rounded-3 p-4 w-100"
              style={{
                backgroundColor: watchBg,
                color: watchFont,
              }}
            >
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <img
                  src={logoPreview || "/connect-logo.png"}
                  alt="logo preview"
                  className="partner-preview-logo mb-3"
                />
                <h5 className="fw-bold mb-2">{watchName || "Business Name"}</h5>
                <p className="mb-0 small">
                  {watchDescription || "Description will appear here"}
                </p>
              </div>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default CreatePartnerModal;
